/* eslint-disable no-nested-ternary */
/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-unstable-nested-components */
import { lightFormat } from "date-fns";
import compareAsc from "date-fns/compareAsc";
import parseISO from "date-fns/parseISO";
import type { SeriesAreaOptions, SeriesColumnOptions } from "highcharts";
import { sum, uniqBy } from "lodash";
import { BarChartIcon } from "lucide-react";
import type { SetStateAction } from "react";
import { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";

import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import darkNavigatorStyles from "components/charts/modern/highcharts/darkNavigatorStyles";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import type { AxisScaleType } from "components/charts/modern/inputs/AxisScaleSelector";
import { AreaBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import useRootIssuer from "components/charts/modern/overlay/useRootIssuer";
import { SectionJumpIdentifiers } from "components/company/SectionJumpIdentifiers";
import FullBleedButton from "components/financials/modern/FullBleedButton";
import SelectStatementType from "components/financials/modern/SelectStatementType";
import { useFinancialsChart } from "components/financials/modern/data/hooks";
import type { IFinancialsSheetQuery } from "components/financials/modern/graphql/__generated__/FinancialsSheetQuery.generated";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import IndicatorsButtonArray from "components/financials/modern/indicators/IndicatorsButtonArray";
import SelectArbitraryIndicatorDebouncer from "components/financials/modern/indicators/SelectArbitraryIndicatorDebouncer";
import SelectArbitraryIndicatorPopover from "components/financials/modern/indicators/SelectArbitraryIndicatorPopover";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import getPlatformVerticalByIssuer from "components/financials/modern/utils/platformVertical";
import { SpanPixelBeacon } from "components/ui/ScrollJump";
import classNames from "components/ui/classNames";
import { defaultColorScheme } from "components/ui/colors/createColorScheme";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";

import useSelfie from "lib/hooks/useSelfie";

import type { IIndicatorDataSource, IPlatformVertical, ISecurityCountry } from "types/graphqlTypes";
import { IFinancialsStatementType } from "types/graphqlTypes";

interface FinancialsChartProps {
  vertical: IPlatformVertical;
  tickerSymbol: string;
  country: ISecurityCountry;
  indicatorDataSource: IIndicatorDataSource;
  currency?: string;
  statementType?: IFinancialsStatementType;
  interim?: string | null;
  arbitraryIndicators: IIndicatorFieldsFragment[];
  selectedIndicators: IIndicatorFieldsFragment[];
  templateId?: string | null;
  onChangeSelectedIndicators: (setState: SetStateAction<IIndicatorFieldsFragment[]>) => void;
  onChangeStatementType?: (statementType: IFinancialsStatementType) => void;
  onChangeInterim?: (interim: string | null) => void;
}

export default function FinancialsChart({
  vertical,
  tickerSymbol,
  country,
  indicatorDataSource,
  statementType,
  interim,
  currency = "USD",
  arbitraryIndicators,
  selectedIndicators,
  templateId,
  onChangeSelectedIndicators,
  onChangeStatementType,
  onChangeInterim,
}: FinancialsChartProps) {
  const issuer = useRootIssuer();
  const platformVertical = getPlatformVerticalByIssuer(issuer, indicatorDataSource);
  const { loading, financialsSheet, financialsTemplate } = useFinancialsChart({
    tickerSymbol,
    country,
    statementType: statementType || IFinancialsStatementType.Annual,
    templateId: templateId || null,
    currency,
    arbitraryIndicators: selectedIndicators,
    interim: interim || null,
    vertical: platformVertical,
    indicatorDataSource,
  });

  return (
    <Chart
      vertical={vertical}
      loading={loading}
      financialsSheet={financialsSheet}
      financialsTemplate={financialsTemplate}
      indicatorDataSource={indicatorDataSource}
      selectedIndicators={selectedIndicators}
      arbitraryIndicators={arbitraryIndicators}
      onSetSelectedIndicators={onChangeSelectedIndicators}
      interim={interim}
      statementType={statementType}
      onChangeStatementType={onChangeStatementType}
      onChangeInterim={onChangeInterim}
    />
  );
}

interface ChartProps {
  vertical: IPlatformVertical;
  loading: boolean;
  financialsSheet: IFinancialsSheetQuery["financialsSheet"] | null;
  financialsTemplate: IFinancialsSheetQuery["financialsSheet"]["systemTemplate"] | null;
  selectedIndicators: IIndicatorFieldsFragment[];
  arbitraryIndicators: IIndicatorFieldsFragment[];
  indicatorDataSource: IIndicatorDataSource;
  statementType?: IFinancialsStatementType;
  interim?: string | null;
  onSetSelectedIndicators: (setState: SetStateAction<IIndicatorFieldsFragment[]>) => void;
  onChangeStatementType?: (statementType: IFinancialsStatementType) => void;
  onChangeInterim?: (interim: string | null) => void;
}

function Chart({
  vertical,
  loading,
  financialsSheet,
  financialsTemplate,
  indicatorDataSource,
  statementType,
  interim,
  selectedIndicators,
  arbitraryIndicators,
  onSetSelectedIndicators,
  onChangeStatementType,
  onChangeInterim,
}: ChartProps) {
  const intl = useIntl();
  const viewer = useSelfie();
  const theme = useTheme();
  const [plotType, setPlotType] = useState<"area" | "column">("area");

  return (
    <div className="flex items-start space-x-6">
      <div className="flex w-full flex-col space-y-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[_1fr,_1fr]">
            <div className="flex flex-wrap items-center gap-4 sm:flex-nowrap">
              <SelectArbitraryIndicatorDebouncer
                selectedIndicators={selectedIndicators}
                onToggleIndicator={(indicator, isSelected) => {
                  const nextSelectedIndicators = isSelected
                    ? selectedIndicators.filter(({ objectId }) => objectId !== indicator.objectId)
                    : [...selectedIndicators.slice(0, 1), indicator];

                  onSetSelectedIndicators(nextSelectedIndicators);
                }}
              >
                {({ selectedIndicators, onToggleIndicator }) => (
                  <SelectArbitraryIndicatorPopover
                    vertical={vertical}
                    indicatorDataSource={indicatorDataSource}
                    mode="TOGGLE"
                    limit={2}
                    selectedIndicators={selectedIndicators}
                    onToggleIndicator={onToggleIndicator}
                  />
                )}
              </SelectArbitraryIndicatorDebouncer>

              {statementType !== IFinancialsStatementType.Monthly && (
                <SelectStatementType
                  disabled={loading || !viewer?.isPremium}
                  interim={interim}
                  statementType={statementType}
                  onChangeInterim={onChangeInterim}
                  onChangeStatementType={onChangeStatementType}
                />
              )}

              <AreaBarPlotTypeSelector value={plotType} onChange={setPlotType} />
            </div>

            <div className="hidden justify-end gap-4 lg:flex">
              <FullBleedButton />
            </div>
          </div>

          {((financialsTemplate && financialsTemplate.indicators.length > 0) || arbitraryIndicators.length > 0) && (
            <IndicatorsButtonArray
              selectedIndicators={selectedIndicators}
              indicators={uniqBy(
                [...(financialsTemplate?.indicators || []), ...arbitraryIndicators, ...selectedIndicators],
                "objectId",
              )}
              onToggleIndicator={(indicator, isSelected) => {
                const nextSelectedIndicators = isSelected
                  ? selectedIndicators.filter(({ objectId }) => objectId !== indicator.objectId)
                  : [...selectedIndicators.slice(0, 1), indicator];

                onSetSelectedIndicators?.(nextSelectedIndicators);
              }}
            />
          )}

          {(!financialsSheet || financialsSheet?.indicators.length === 0) && (
            <div
              className={classNames(
                "rounded-xl bg-amber-500/5 p-4 text-center text-sm tracking-wide text-amber-600",
                "space-y-4",
              )}
            >
              <BarChartIcon className="inline-flex h-6 w-6" />
              <p className="text-center font-medium">
                <FormattedMessage
                  defaultMessage="Selecione ao menos um indicador para desenhar um gráfico."
                  id="JdGr9d"
                />
              </p>
            </div>
          )}

          {financialsSheet && financialsSheet.indicators.length > 0 && (
            <>
              <SpanPixelBeacon offset={-360} id={SectionJumpIdentifiers.Financials} />
              <HighchartsChart
                immutable={true}
                options={getChartOptions(intl, plotType, "linear", theme as "dark" | "light", financialsSheet)}
                className="h-96 w-80 sm:w-full"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function getChartOptions(
  intl: IntlShape,
  plotType: "column" | "area",
  axisScaleType: AxisScaleType,
  theme: "light" | "dark",
  financialsSheet: IFinancialsSheetQuery["financialsSheet"],
) {
  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const isMulti = financialsSheet.indicators.length > 1;
  const isNegative = financialsSheet.indicators.every((indicator) => {
    return indicator.series.every((node) => {
      return isValueNode(node) ? node.value <= 0 : true;
    });
  });
  const columnThemes = financialsSheet.indicators.map((indicator) => indicator.columnTheme);
  const backgroundColors = defaultColorScheme.redirectColorsAsHex(columnThemes.map((theme) => theme.backgroundColor));
  const textColors = defaultColorScheme.redirectColorsAsHex(columnThemes.map((theme) => theme.textColor));
  const betterColumnThemes = columnThemes.map((theme, index) => {
    return {
      ...theme,
      backgroundColor: backgroundColors[index],
      textColor: textColors[index],
    };
  });

  const series: (SeriesAreaOptions | SeriesColumnOptions)[] = financialsSheet.indicators
    .slice(0, 2)
    .map((indicator, indicatorIndex) => {
      const columnTheme = betterColumnThemes[indicatorIndex];
      const backgroundColor = defaultColorScheme.remapColor(columnTheme.backgroundColor, theme);
      const lineColor = defaultColorScheme.shadingColor(columnTheme.backgroundColor, indicatorIndex, theme);
      const documentation = getIndicatorDocumentation(intl, indicator);

      const defaultOptions: SeriesAreaOptions | SeriesColumnOptions = {
        showInNavigator: indicatorIndex === 0,
        type: plotType,
        animation: false,
        name: documentation.name,
        yAxis: indicatorIndex,
        color: backgroundColor,
        lineColor,
        lineWidth: 2.5,
        data: financialsSheet.reportDates
          .map((date, dataIndex) => {
            const absoluteSeriesValues = financialsSheet.indicators[indicatorIndex].series.map((resolvedValue) => {
              return isValueNode(resolvedValue) ? Math.abs(resolvedValue.value) : 0;
            });

            const node = financialsSheet.indicators[indicatorIndex].series[dataIndex];
            const resolvedValue = isValueNode(node) ? node.value : 0;
            const totalSum = sum(absoluteSeriesValues);
            const value = isMulti
              ? (Math.abs(resolvedValue) / totalSum) * 100
              : isNegative
                ? Math.abs(resolvedValue)
                : resolvedValue;

            return {
              x: parseISO(date).getTime(),
              y: value,
              absoluteY: resolvedValue,
              custom: {
                indicatorName: documentation.abbreviation,
                asOfDate: date,
                value,
                formattedValue: financialsSheet.indicators[indicatorIndex].series[dataIndex].formattedValue,
              },
            };
          })
          .sort((a, b) => compareAsc(a.x, b.x)),
        custom: {
          backgroundColor: defaultColorScheme.remapColor(columnTheme.backgroundColor, theme, 300),
          textColor: defaultColorScheme.remapColor(columnTheme.textColor, theme, 950),
        },
        marker: {
          width: 8,
          height: 8,
          lineWidth: 1,
          fillColor: lineColor,
          color: backgroundColor,
          lineColor: lineColor,
          symbol: "circle",
        },
      };

      if (plotType === "column") {
        const borderColor = defaultColorScheme.shadingColor(indicator.columnTheme.textColor, indicatorIndex, theme);
        const columnOptions = {
          borderColor,
          borderWidth: 1.25,
          states: {
            hover: {
              color: borderColor,
              borderColor: borderColor,
              backgroundColor: backgroundColor,
            },
          },
        };
        return {
          ...columnOptions,
          ...defaultOptions,
        };
      }

      return defaultOptions;
    });

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        type: "x",
        enabled: true,
      },
      panKey: "shift",
      alignTicks: true,
      animation: false,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineDashStyle: "Solid",
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 0,
      lineColor: slateOrDarkSlate,
      tickWidth: 1,
      tickColor: slateOrDarkSlate,
      scrollbar: {
        enabled: false,
      },
      labels: {
        style: {
          color: labelsColor,
          fontWeight: "500",
          fontSize: "12px",
          fontFamily: aeonik.style.fontFamily,
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: theme === "light" ? colors.slate[800] : colors.slate[500],
      },
    },
    yAxis: financialsSheet.indicators.slice(0, 2).map((indicator, index) => {
      const values = indicator.series.map((node) => {
        return isValueNode(node) ? node.value : 0;
      });

      const minValue = values.length > 0 ? Math.min(...values) : 0;
      const allValuesBelowZero = values.every((value) => value <= 0);
      const valuesBelowZero = [...values.sort((a, b) => b - a)].filter((value) => value < 0);
      const firstValueBelowZero = valuesBelowZero[0];

      return {
        type: axisScaleType,
        min: isMulti || isNegative ? 0 : minValue,
        plotLines:
          financialsSheet.indicators.length === 1 && !allValuesBelowZero
            ? [
                {
                  color: theme === "dark" ? colors.slate[600] : colors.slate[950],
                  value: firstValueBelowZero,
                  dashStyle: "Dash",
                  width: 1.5,
                },
              ]
            : undefined,
        gridLineWidth: 0,
        gridLineColor: slateOrDarkSlate,
        lineWidth: 1,
        lineColor: slateOrDarkSlate,
        crosshair: {
          snap: true,
          dashStyle: "Dash",
          color: theme === "light" ? colors.slate[800] : colors.slate[500],
        },
        labels: {
          enabled: !isMulti,
          align: "center",
          style: {
            color: labelsColor,
            fontFamily: aeonik.style.fontFamily,
            fontWeight: "500",
            fontSize: "12px",
          },
          formatter: function (this) {
            const actualValue = this.value;
            const isPercent = indicator.series.find(
              (node) => node.__typename === "FinancialIndicatorSeriesPercentNode",
            );

            if (isPercent || isMulti) {
              const percentageFormatter = new Intl.NumberFormat("en-US", {
                style: "percent",
                maximumFractionDigits: 2,
              });

              return percentageFormatter.format(parseFloat(String(actualValue)));
            }

            const valueFormatter = new Intl.NumberFormat("en", {
              notation: "compact",
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            });
            return valueFormatter.format(parseFloat(String(actualValue)));
          },
        },
        tickWidth: 0,
        showFirstLabel: true,
        showLastLabel: false,
        scrollbar: {
          enabled: false,
        },
        opposite: index > 0,
      };
    }),
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      ...(theme === "dark" ? darkNavigatorStyles : navigatorStyles),
      yAxis: {
        ...navigatorStyles.yAxis,
        type: axisScaleType,
      },
    },
    series: [...series],
    tooltip: {
      followPointer: true,
      followTouchMove: false,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: theme === "dark" ? colors.slate[800] : colors.white,
      borderColor: theme === "dark" ? colors.slate[950] : colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        if (!this.points || this.points.length === 0) {
          return "";
        }

        const point = (this.point as any).custom as Record<string, any> | null | undefined;
        if (!this.key || !point) {
          return "";
        }

        return renderToStaticMarkup(
          <div className="z-10 flex flex-col space-y-3 p-2 font-mono">
            <span
              className={classNames(
                "inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600",
                "dark:bg-slate-950 dark:text-orange-300",
              )}
            >
              {lightFormat(new Date(point.asOfDate), "dd/MM/yyyy")}
            </span>

            {this.points
              .sort((a, b) => {
                return (b.y || 0) - (a.y || 0);
              })
              .map(({ key, series, point }) => {
                const node = (point as any).custom as Record<string, any> | null | undefined;
                if (!key || !node) {
                  return "";
                }
                const backgroundColor = String((series.userOptions as SeriesAreaOptions).custom?.backgroundColor);
                const textColor = String((series.userOptions as SeriesAreaOptions).custom?.textColor);
                return (
                  <div key={key} className="flex items-center justify-between space-x-3">
                    <span
                      className="rounded-md px-2 py-1 font-mono text-xs font-bold"
                      style={{
                        backgroundColor,
                        color: textColor,
                      }}
                    >
                      {node.indicatorName}
                    </span>

                    <span className="font-mono font-semibold dark:text-slate-300">{node.formattedValue}</span>
                  </div>
                );
              })}
          </div>,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: theme === "dark" ? colors.slate[400] : colors.slate[500],
            color: theme === "dark" ? colors.slate[400] : colors.slate[50],
            lineWidth: 2,
          },
        },
      },
      area: {
        stacking: "normal",
        lineWidth: 2,
        dataLabels: {
          enabled: false,
        },
      },
      column: {
        groupPadding: series.length ? 0.15 : 0,
        borderRadius: {
          where: "end",
          radius: 8,
          scope: "stack",
        },
      },
    },
  };

  return options;
}

function isValueNode<BracketNode extends IFinancialsSheetQuery["financialsSheet"]["indicators"][0]["series"][0]>(
  node: BracketNode,
): node is Exclude<
  BracketNode,
  { __typename: "FinancialIndicatorSeriesTextNode" | "FinancialIndicatorSeriesSkeletonNode" }
> {
  return (
    node.__typename !== "FinancialIndicatorSeriesTextNode" && node.__typename !== "FinancialIndicatorSeriesSkeletonNode"
  );
}
